<script setup>
import {
  computed,
  ref,
  onMounted,
  onUnmounted,
  inject,
  nextTick,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { usePipelineCard } from 'dashboard/composables/usePipelineCard';
import { usePipelineDates } from 'dashboard/composables/usePipelineDates';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import { getCardValue } from '../helpers/columnFilterMatchers';
// Card-2B (2026-04-22): vOnClickOutside no template root substitui
// document.addEventListener (100 cards = 100 listeners no document antes).
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  draggable: {
    type: Boolean,
    default: true,
  },
  // Nome do stage atual; usado para suprimir labels redundantes com o
  // título da coluna (ex.: card em "Qualificação" não mostra label "qualificado").
  stageName: {
    type: String,
    default: '',
  },
});
const emit = defineEmits([
  'click',
  'edit',
  'delete',
  'openDetails',
  'dragstart',
  'dragend',
  'statusChange',
  'openSequences',
]);
// Injetar o map de labels fornecido pelo componente pai
const labelsMap = inject('labelsMap', {});
import { emitter } from 'shared/helpers/mitt';

// Drag state
const isDragging = ref(false);

// Title truncation detection (Phase 2 - UX Improvements)
const titleElement = ref(null);

const { t } = useI18n();
const router = useRouter();
const store = useStore();
// Audit 2026-05-07 — feature flag pipeline_sequences. Quando desligada para
// esta account, o badge de sequência some e nenhum fetch é disparado (evita
// 404s e ruído no console se SuperAdmin desligar a feature em runtime).
const { isCloudFeatureEnabled } = useAccount();
const isSequencesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_SEQUENCES)
);
const { showAlert } = useAlert();

// Status menu
const showStatusMenu = ref(false);

// Actions menu
const showActionsMenu = ref(false);

// Priority dropdown
const showPriorityDropdown = ref(false);
const priorityDropdownStyle = ref({});
const priorityButtonRef = ref(null);

// Agent dropdown — UX 2026-04-22: trocar agente inline (antes click
// no agente caía pro article e abria detalhes do card sem affordance
// de mudança).
const showAgentDropdown = ref(false);
const agentDropdownStyle = ref({});
const agentButtonRef = ref(null);
const agentSearchQuery = ref('');

// Composables
const { hasConversation, isAssigned, displayTitle } = usePipelineCard(
  computed(() => props.card)
);

// Date composable for relative dates
const {
  cardLifetime,
  deadline,
  deadlineBadgeClasses,
  isOverdue,
  scheduledAt,
  hasScheduledDate,
} = usePipelineDates(computed(() => props.card));

// Card status
const cardStatus = computed(() => {
  return props.card.status || 'open';
});

const statusLabel = computed(() => {
  const status = cardStatus.value;
  const statusMap = {
    open: t('PIPELINE.CARD.STATUS.OPEN'),
    won: t('PIPELINE.CARD.STATUS.WON'),
    lost: t('PIPELINE.CARD.STATUS.LOST'),
  };
  return statusMap[status] || t('PIPELINE.CARD.STATUS.OPEN');
});

const statusClasses = computed(() => {
  const status = cardStatus.value;
  const statusStyles = {
    open: 'bg-n-alpha-1 text-n-slate-11 border-n-weak',
    won: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    lost: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
  };
  return statusStyles[status] || statusStyles.open;
});

// Status options for menu
// Verificar se etapas ganho/perdido estão habilitadas
const enableWonLostStages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  return pipeline?.settings?.enable_won_lost_stages !== false;
});

// Status options for menu (filtra status atual e won/lost se desabilitado)
const statusOptions = computed(() => {
  const allOptions = [{ value: 'open', label: t('PIPELINE.CARD.STATUS.OPEN') }];

  // Só adiciona opções de won/lost se habilitado
  if (enableWonLostStages.value) {
    allOptions.push(
      { value: 'won', label: t('PIPELINE.CARD.STATUS.WON') },
      { value: 'lost', label: t('PIPELINE.CARD.STATUS.LOST') }
    );
  }

  // Filtra o status atual para não mostrar opção redundante
  return allOptions.filter(option => option.value !== cardStatus.value);
});

// Determina se o badge de status pode ser clicável (bidirecional: open↔won/lost)
const canChangeStatus = computed(() => {
  return enableWonLostStages.value;
});

// Formatted value for display.
// Ciclo 6 (2026-06-10): paridade com getCardValue (expected_revenue →
// item_details.value), a mesma semântica de filtros/sort/total da coluna.
// Antes lia só item_details.value — cards com valor server-side apenas em
// expected_revenue não mostravam o badge "R$" (achado QA do ciclo 5).
// Valor 0/ausente continua ocultando o badge (comportamento preservado).
const formattedValue = computed(() => {
  const value = getCardValue(props.card);
  if (!value) return null;
  // Moeda: item_details.currency ({code, locale}) → currency raiz (string,
  // coluna DB — cards de API/import com valor em expected_revenue podem não
  // ter o mirror em item_details; achado Codex ciclo 6) → BRL.
  const detailsCurrency = props.card?.item_details?.currency;
  const rootCurrency =
    typeof props.card?.currency === 'string' && props.card.currency
      ? { code: props.card.currency, locale: 'pt-BR' }
      : null;
  const currency = detailsCurrency ||
    rootCurrency || {
      code: 'BRL',
      locale: 'pt-BR',
    };
  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
    }).format(value);
  } catch {
    return `R$ ${value}`;
  }
});

// Get customer name from conversation contact
const customerName = computed(() => {
  // Priority: conversation contact name
  const conversationContact = props.card?.item_details?.conversation?.contact;
  if (conversationContact?.name) return conversationContact.name;
  // Fallback: card contact
  if (props.card?.contact?.name) return props.card.contact.name;
  // Fallback: title
  return displayTitle.value;
});

// Get inbox name from conversation
const inboxName = computed(() => {
  return (
    props.card?.item_details?.conversation?.inbox?.name ||
    props.card?.item_details?.inbox_name ||
    null
  );
});

// Description preview (truncated)
const isAutoDescription = computed(() => {
  const desc = props.card?.item_details?.description || '';
  return /^(Criado automaticamente|Added from conversation|Auto-created)/i.test(
    desc.trim()
  );
});

const descriptionPreview = computed(() => {
  let description = props.card?.item_details?.description;
  if (!description) return null;
  // Clean up #null / #undefined placeholders from older cards
  description = description.replace(/#(null|undefined)/g, '');
  // Hide auto-generated descriptions when conversation link already exists
  if (isAutoDescription.value && hasConversation.value) return null;
  const maxLength = 80;
  if (description.length <= maxLength) return description;
  return description.substring(0, maxLength) + '...';
});

// ARIA label for accessibility (Task 2.3)
const cardAriaLabel = computed(() => {
  const parts = [customerName.value];

  if (formattedValue.value) {
    parts.push(formattedValue.value);
  }

  if (deadline.value?.text) {
    parts.push(deadline.value.text);
  }

  if (statusLabel.value) {
    parts.push(statusLabel.value);
  }

  return parts.join(', ');
});

// Priority configuration
const priority = computed(() => {
  return props.card?.item_details?.priority || props.card?.priority || 'none';
});

// Left border accent by priority
const priorityBorderClass = computed(() => {
  const classes = {
    urgent: 'border-l-[3px] border-l-ruby-9',
    high: 'border-l-[3px] border-l-orange-9',
    medium: 'border-l-[3px] border-l-amber-9',
    low: 'border-l-[3px] border-l-blue-9',
  };
  return classes[priority.value] || '';
});

// All priority options (used for dropdown + icon rendering)
const priorityOptions = [
  {
    value: 'urgent',
    icon: 'i-lucide-flame',
    color: 'text-n-ruby-11',
    bgColor: 'bg-n-ruby-3 border-n-ruby-6',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.URGENT');
    },
  },
  {
    value: 'high',
    icon: 'i-lucide-chevrons-up',
    color: 'text-orange-11',
    bgColor: 'bg-orange-3 border-orange-6',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.HIGH');
    },
  },
  {
    value: 'medium',
    icon: 'i-lucide-minus',
    color: 'text-n-amber-11',
    bgColor: 'bg-n-amber-3 border-n-amber-6',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.MEDIUM');
    },
  },
  {
    value: 'low',
    icon: 'i-lucide-chevrons-down',
    color: 'text-blue-11',
    bgColor: 'bg-blue-3 border-blue-6',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.LOW');
    },
  },
  {
    value: 'none',
    icon: 'i-lucide-circle-dashed',
    color: 'text-n-slate-9',
    bgColor: 'bg-n-alpha-1 border-n-weak',
    get label() {
      return t('PIPELINE.CARD.PRIORITY.NONE');
    },
  },
];

const priorityConfig = computed(() => {
  return (
    priorityOptions.find(o => o.value === priority.value) || priorityOptions[4]
  );
});

// Agent info
const agentInfo = computed(() => {
  // Check owner first (set by backend from owner_id relation)
  if (props.card?.owner) {
    return props.card.owner;
  }
  // Check item_details.agent (set during card creation)
  if (props.card?.item_details?.agent) {
    return props.card.item_details.agent;
  }
  // Fallback to assignee/assigned_agent for older cards
  if (!isAssigned.value) return null;
  return props.card.assignee || props.card.assigned_agent || null;
});

const avatarUrl = computed(() => {
  // Primary: item_details.contact_thumbnail (new flat structure)
  const itemDetails = props.card?.item_details;
  if (itemDetails?.contact_thumbnail && itemDetails.contact_thumbnail.trim()) {
    return itemDetails.contact_thumbnail;
  }
  // Legacy: conversation.contact.thumbnail (old nested structure)
  const conversationContact = itemDetails?.conversation?.contact;
  if (conversationContact?.thumbnail && conversationContact.thumbnail.trim()) {
    return conversationContact.thumbnail;
  }
  if (
    conversationContact?.avatar_url &&
    conversationContact.avatar_url.trim()
  ) {
    return conversationContact.avatar_url;
  }
  // Fallback: card contact thumbnail (from API response)
  if (props.card?.contact?.thumbnail && props.card.contact.thumbnail.trim()) {
    return props.card.contact.thumbnail;
  }
  if (props.card?.contact?.avatar_url && props.card.contact.avatar_url.trim()) {
    return props.card.contact.avatar_url;
  }
  return null;
});

const initials = computed(() => {
  const name = customerName.value || 'N';
  return name.charAt(0).toUpperCase();
});

// Avatar error fallback
const avatarLoadError = ref(false);
const handleAvatarError = () => {
  avatarLoadError.value = true;
};

// Check if title is truncated - measured on hover to avoid layout thrashing
const isTitleTruncated = ref(false);
const checkTruncation = () => {
  if (titleElement.value) {
    isTitleTruncated.value =
      titleElement.value.scrollHeight > titleElement.value.clientHeight;
  }
};

// Conversation navigation
const conversationUrl = computed(() => {
  if (!hasConversation.value) return null;
  const accountId = props.card?.account_id || store.getters.getCurrentAccountId;
  const conversationId = props.card?.conversation_display_id;
  if (!accountId || !conversationId) return null;
  return `/app/accounts/${accountId}/conversations/${conversationId}`;
});

// Labels da conversa vinculada
const conversationLabels = computed(() => {
  return props.card?.item_details?.conversation?.label_list || [];
});

// Card-4.4 (2026-04-29): suprimir labels que repetem o nome do stage
// atual (ex.: card em "Qualificação" não precisa carregar a tag
// "qualificado" — sobrecarga cognitiva sem ganho informacional).
// Comparação case + whitespace + diacrítico-insensitive.
const normalize = str =>
  String(str || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();

const filteredConversationLabels = computed(() => {
  const stageKey = normalize(props.stageName);
  if (!stageKey) return conversationLabels.value;
  return conversationLabels.value.filter(
    label => normalize(label) !== stageKey
  );
});

// Card-4.8 (2026-04-29): pedido do cliente — mostrar TODAS as labels,
// quebrando em múltiplas linhas quando não couberem. Antes (Card-4.3)
// limitava a 3 visíveis + "+N" pill para preservar altura constante;
// trade-off agora favorece visibilidade total das tags (UX > altura
// previsível, já que cards já têm altura variável por outras razões).
const visibleLabels = computed(() => filteredConversationLabels.value);

// Round-3 (2026-04-25): hover em QUALQUER label (visível ou no "+N")
// mostra a lista completa de etiquetas vinculadas. Antes só o "+N"
// expunha as ocultas — o que cortava o contexto ao agente que queria
// ver tudo de uma vez sem clicar pra abrir o card.
const allLabelsText = computed(() =>
  filteredConversationLabels.value.length
    ? filteredConversationLabels.value.join(', ')
    : ''
);

// Função para escurecer a cor (para texto sobre fundo claro)
const darkenColor = color => {
  if (!color) return 'var(--color-n-slate-10)';
  // Remove # se presente
  const hex = color.replace('#', '');
  // Converte para RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Escurece em 30%
  const factor = 0.7;
  const dr = Math.round(r * factor);
  const dg = Math.round(g * factor);
  const db = Math.round(b * factor);
  return `rgb(${dr}, ${dg}, ${db})`;
};

// Obtém a cor da label do mapa ou retorna cor padrão
const getLabelColor = label => {
  const mapValue = labelsMap?.value || labelsMap;
  return mapValue?.[label] || null;
};

// ── Collapse / expand ──────────────────────────────────────────────────────
// UX Card-Q1.2 (2026-04-22): inject estado centralizado do PipelineView
// (lê localStorage 1× por board mount, não 3× por card como antes).
// Antes: cada card lia + parseava localStorage 2× no mount e 1× a cada
// toggle. Com 100 cards = 200+ reads sincronas no boot.
const collapsedItems = inject('collapsedItems', ref({}));
const updateCollapsedItem = inject('updateCollapsedItem', () => {});

const isCollapsed = ref(false);

// Watcher reativo: aplica do parent injetado.
watch(
  () => collapsedItems.value[props.card.id],
  newCollapsed => {
    if (newCollapsed !== undefined) isCollapsed.value = newCollapsed;
  },
  { immediate: true }
);

const toggleCollapse = event => {
  event.stopPropagation();
  isCollapsed.value = !isCollapsed.value;
  updateCollapsedItem(props.card.id, isCollapsed.value);
};

// Event handlers
const handleCardClick = event => {
  // 5.9: Alt+click toggles collapse/expand instead of opening the card.
  // Restricted to a real mouse click so Alt+Enter on the focused card still opens.
  if (event?.type === 'click' && event.altKey) {
    event.preventDefault();
    toggleCollapse(event);
    return;
  }
  emit('click', props.card);
};

// 5.4: surface the next pending checklist item directly on the kanban card.
// The board index ships a lightweight `next_checklist_item` ({ text }) — the
// full checklist array is not on board cards. Fall back to scanning the full
// checklist when present (e.g. a card hydrated with item_details.checklist).
const nextChecklistItem = computed(() => {
  const projected = props.card?.next_checklist_item;
  if (projected?.text) return projected;
  const checklist = props.card?.item_details?.checklist;
  if (!Array.isArray(checklist)) return null;
  return checklist.find(i => i && !i.completed) || null;
});

const handleOpenDetails = () => {
  emit('openDetails', props.card);
};

const handleDragStart = event => {
  if (!props.draggable) {
    event.preventDefault();
    return;
  }

  // Stop propagation to prevent column drag from interfering
  event.stopPropagation();

  isDragging.value = true;
  event.dataTransfer.setData('text/plain', props.card.id.toString());
  event.dataTransfer.effectAllowed = 'move';

  // Set drag image to the card element for better visual feedback
  const cardElement = event.currentTarget;
  if (cardElement) {
    event.dataTransfer.setDragImage(cardElement, 20, 20);
  }

  emit('dragstart', event);
};

const handleDragEnd = () => {
  isDragging.value = false;
  emit('dragend');
};

const toggleStatusMenu = () => {
  showStatusMenu.value = !showStatusMenu.value;
};

const changeStatus = newStatus => {
  showStatusMenu.value = false;
  if (newStatus !== cardStatus.value) {
    emit('statusChange', { card: props.card, status: newStatus });
  }
};

// Open conversation in inbox
const openConversation = event => {
  event.stopPropagation();
  if (conversationUrl.value) {
    router.push(conversationUrl.value);
  }
};

// Actions menu handlers
const toggleActionsMenu = event => {
  event.stopPropagation();

  const wasOpen = showActionsMenu.value;
  // Close ALL pipeline dropdowns (column menus, header menus, other cards)
  emitter.emit('closeAllPipelineDropdowns');
  emitter.emit('closeAllCardDropdowns', props.card.id);
  if (!wasOpen) {
    showActionsMenu.value = true;
  }
};

const closeActionsMenu = () => {
  showActionsMenu.value = false;
};

// Priority dropdown handlers
const togglePriorityDropdown = event => {
  event.stopPropagation();
  const wasOpen = showPriorityDropdown.value;
  // Close ALL pipeline dropdowns (column menus, header menus, other cards)
  emitter.emit('closeAllPipelineDropdowns');
  emitter.emit('closeAllCardDropdowns', props.card.id);
  if (!wasOpen) {
    showPriorityDropdown.value = true;
    nextTick(() => {
      if (priorityButtonRef.value) {
        const rect = priorityButtonRef.value.getBoundingClientRect();
        priorityDropdownStyle.value = {
          top: `${rect.bottom + 4}px`,
          left: `${rect.left}px`,
        };
      }
    });
  }
};

const handleSelectPriority = async newPriority => {
  showPriorityDropdown.value = false;
  if (newPriority === priority.value) return;
  const newItemDetails = {
    ...props.card.item_details,
    priority: newPriority === 'none' ? null : newPriority,
  };
  await store.dispatch('pipelineCards/updateItemDetails', {
    itemId: props.card.id,
    details: { item_details: newItemDetails },
  });
};

// Agent dropdown handlers — UX 2026-04-22
const availableAgents = computed(() => {
  return store.getters['agents/getVerifiedAgents'] || [];
});

const filteredAgents = computed(() => {
  const q = agentSearchQuery.value.trim().toLowerCase();
  if (!q) return availableAgents.value;
  return availableAgents.value.filter(a =>
    (a.name || a.email || '').toLowerCase().includes(q)
  );
});

const toggleAgentDropdown = event => {
  event.stopPropagation();
  const wasOpen = showAgentDropdown.value;
  emitter.emit('closeAllPipelineDropdowns');
  emitter.emit('closeAllCardDropdowns', props.card.id);
  if (!wasOpen) {
    agentSearchQuery.value = '';
    showAgentDropdown.value = true;
    nextTick(() => {
      if (agentButtonRef.value) {
        const rect = agentButtonRef.value.getBoundingClientRect();
        // Posiciona ABOVE the agent button (footer fica na base do card).
        agentDropdownStyle.value = {
          bottom: `${window.innerHeight - rect.top + 4}px`,
          left: `${rect.left}px`,
        };
      }
    });
  }
};

const handleSelectAgent = async agentId => {
  showAgentDropdown.value = false;
  // Se clicou no mesmo agente já atribuído, no-op
  if (agentInfo.value && agentInfo.value.id === agentId) return;
  try {
    await store.dispatch('pipelineCards/updateItemOwner', {
      itemId: props.card.id,
      ownerId: agentId,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[PipelineCard] handleSelectAgent error:', e);
    showAlert(t('PIPELINE.MESSAGES.AGENT_ASSIGN_ERROR'));
  }
};

const handleUnassignAgent = async () => {
  showAgentDropdown.value = false;
  if (!agentInfo.value) return;
  try {
    await store.dispatch('pipelineCards/updateItemOwner', {
      itemId: props.card.id,
      ownerId: null,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[PipelineCard] handleUnassignAgent error:', e);
    showAlert(t('PIPELINE.MESSAGES.AGENT_ASSIGN_ERROR'));
  }
};

// Close all dropdowns in this card (used by both card-level and pipeline-level events)
const closeAllCardDropdowns = () => {
  showActionsMenu.value = false;
  showStatusMenu.value = false;
  showPriorityDropdown.value = false;
  showAgentDropdown.value = false;
};

// Close dropdowns when clicking outside or when another card opens its dropdown
const handleCloseAllDropdowns = excludeCardId => {
  if (excludeCardId !== props.card.id) {
    closeAllCardDropdowns();
  }
};

// Close on global pipeline event (column menu, header dropdowns, etc.)
const handleClosePipelineDropdowns = () => {
  closeAllCardDropdowns();
};

// Close on click outside
const handleDocumentClick = () => {
  closeAllCardDropdowns();
};

// Card-2B (2026-04-22): vOnClickOutside no template root substitui
// document.addEventListener. Antes 100 cards = 100 listeners no document.
// emitter de closeAllCardDropdowns/closeAllPipelineDropdowns mantido
// — usado para sincronizar fechamento entre cards do mesmo board.
onMounted(() => {
  emitter.on('closeAllCardDropdowns', handleCloseAllDropdowns);
  emitter.on('closeAllPipelineDropdowns', handleClosePipelineDropdowns);
  // Phase 1.2: Lazy-fetch active sequence for badge. No-op if already cached
  // or another fetch is in-flight (guards inside the action).
  // Audit 2026-05-07 — só dispara fetch se:
  //   1. feature pipeline_sequences ligada (zero 404s quando off)
  //   2. card.account_id bate com a route accountId atual (zero requests
  //      cross-account durante transição de rota — Vai Pri / ClickHype bug).
  const routeAcct = (() => {
    const seg = window.location.pathname.split('/');
    const idx = seg.indexOf('accounts');
    return idx === -1 ? null : Number(seg[idx + 1]);
  })();
  const cardAcct = props.card.account_id ? Number(props.card.account_id) : null;
  const cardAccountMatches =
    cardAcct == null || routeAcct == null || cardAcct === routeAcct;
  if (isSequencesEnabled.value && cardAccountMatches) {
    store.dispatch('pipelineCardSequences/fetchForCard', props.card.id);
  }
});

onUnmounted(() => {
  emitter.off('closeAllCardDropdowns', handleCloseAllDropdowns);
  emitter.off('closeAllPipelineDropdowns', handleClosePipelineDropdowns);
});

const handleEdit = event => {
  event.stopPropagation();
  closeActionsMenu();
  emit('edit', props.card);
};

// Card-4.6 (2026-04-29) — click no badge "Prazo" abre o form de
// edição (PipelineCardForm) focado em deadline/forecast_close_date.
// Prazo é propriedade interna do card.
const handleEditDeadline = event => {
  event.stopPropagation();
  emit('edit', props.card);
};

// Card-4.7 (2026-04-29) — click no badge "Agendado" navega para a
// view Agenda do pipeline, onde o agente vê todos os agendamentos em
// calendário. scheduled_at é semanticamente um compromisso (sync com
// Google Calendar via google_event_id), não uma propriedade interna.
//
// Comportamento alternativo (futuro): ?highlight=cardId no query string
// para destacar o item específico no calendário.
const handleOpenAgenda = event => {
  event.stopPropagation();
  const route = router.currentRoute.value;
  // Substitui /board ou /list por /agenda mantendo accountId e pipelineSlug
  const newPath = route.fullPath
    .replace(/\/board(\?|$)/, '/agenda$1')
    .replace(/\/list(\?|$)/, '/agenda$1')
    .replace(/\/dashboard(\?|$)/, '/agenda$1');
  if (newPath !== route.fullPath) {
    router.push(
      newPath + (newPath.includes('?') ? '&' : '?') + `card=${props.card.id}`
    );
  } else {
    // Fallback: emit edit (caso a URL não seja convertível)
    emit('edit', props.card);
  }
};

// ── Sequence badge ────────────────────────────────────────────────────────
// Phase 1.2: Reads cached active sequence progress from the Vuex store.
// Data is populated reactively when CardSequencesSection fetches sequences
// (on card detail open). On cold-start (card never opened), falls back to
// an on-demand fetch so the badge eventually shows without blocking render.
const activeSequenceProgress = computed(() =>
  store.getters['pipelineCardSequences/getActiveSequenceForCard'](props.card.id)
);

const progressPercent = computed(() => {
  const seq = activeSequenceProgress.value;
  if (!seq || !seq.total_steps) return 0;
  return Math.round((seq.current_step / seq.total_steps) * 100);
});

const sequenceTooltip = computed(() => {
  const seq = activeSequenceProgress.value;
  if (!seq) return '';
  return t('PIPELINE.SEQUENCES.BADGE_TOOLTIP', {
    name: seq.name || t('PIPELINE.SEQUENCES.UNNAMED'),
    current: seq.current_step,
    total: seq.total_steps,
  });
});

const handleOpenSequences = event => {
  event.stopPropagation();
  emit('openSequences', props.card);
  emit('openDetails', props.card);
};

const handleDelete = event => {
  event.stopPropagation();
  closeActionsMenu();
  emit('delete', props.card);
};

const handleViewDetails = event => {
  event.stopPropagation();
  closeActionsMenu();
  emit('openDetails', props.card);
};
</script>

<template>
  <article
    v-on-click-outside="handleDocumentClick"
    class="pipeline-card group bg-white/90 dark:bg-n-solid-2/85 rounded-2xl p-4 border border-white/40 dark:border-white/10 shadow-sm hover:shadow-xl hover:shadow-black/5 hover:-translate-y-0.5 cursor-pointer transition-all duration-200 select-none relative overflow-visible hover:bg-white dark:hover:bg-n-solid-2 focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:outline-offset-4 motion-reduce:transition-none motion-reduce:transform-none"
    :class="[
      priorityBorderClass,
      {
        'cursor-grab': draggable && !isDragging,
        'cursor-grabbing opacity-50 scale-105 rotate-2 shadow-2xl ring-4 ring-n-brand z-50':
          isDragging,
      },
    ]"
    :draggable="draggable"
    tabindex="0"
    role="listitem"
    :aria-label="cardAriaLabel"
    :aria-describedby="`card-details-${card.id}`"
    @click="handleCardClick"
    @keydown.enter="handleCardClick"
    @keydown.space.prevent="handleOpenDetails"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <!-- Card-4.2 (2026-04-22): drag handle absolute REMOVIDO.
         O `cursor-grab` no card + `border-l-3` da priority já dão
         affordance suficiente — o handle absolute competia com avatar
         e nome em hover. Drag and drop continua funcional via HTML5
         draggable attribute. -->

    <!-- Header — Card-4.5 (2026-04-29):
         layout reorganizado para o nome ocupar 100% da largura do card.
         Antes: [Avatar 40 | Nome | Ações] em linha única, com nome
         espremido entre avatar e 3 botões → nomes longos truncavam.
         Agora: [Avatar 32 + Inbox + Ações] em cima, nome largo embaixo. -->
    <div class="flex flex-col mb-3">
      <!-- Top row: avatar small + inbox + actions -->
      <div class="flex items-center gap-2 mb-2">
        <!-- Avatar -->
        <div
          v-if="avatarUrl && !avatarLoadError"
          class="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/50 dark:ring-white/10 shadow-sm"
        >
          <img
            :src="avatarUrl"
            :alt="customerName"
            class="w-full h-full object-cover"
            @error="handleAvatarError"
          />
        </div>
        <div
          v-else
          class="w-8 h-8 rounded-full bg-gradient-to-br from-n-alpha-2 to-n-alpha-3 flex items-center justify-center flex-shrink-0 ring-2 ring-white/50 dark:ring-white/10 shadow-sm"
        >
          <span class="text-xs font-bold text-n-brand">
            {{ initials }}
          </span>
        </div>

        <!-- Inbox label inline -->
        <p
          v-if="inboxName"
          class="flex-1 min-w-0 text-label-small text-n-slate-10 truncate flex items-center gap-1"
        >
          <FluentIcon
            icon="mail-inbox"
            size="12"
            class="opacity-70 flex-shrink-0"
          />
          <span class="truncate">{{ inboxName }}</span>
        </p>
        <span v-else class="flex-1" aria-hidden="true" />

        <!-- Actions group (priority/collapse/menu) — Card-4.5 (2026-04-29):
             border-l removido pra não cortar visualmente esse top row;
             grupo continua agrupado pelo gap-1. -->
        <div class="flex-shrink-0 flex items-center gap-1">
          <!-- Priority Icon (clickable). Tamanho via tokens BUTTON.sm. -->
          <div
            ref="priorityButtonRef"
            class="flex-shrink-0"
            @click.stop="togglePriorityDropdown"
          >
            <button
              v-tooltip="{
                content: priorityConfig.label,
                delay: { show: 400, hide: 0 },
                placement: 'top',
              }"
              type="button"
              class="w-7 h-7 rounded-full flex items-center justify-center border shadow-sm transition-transform hover:scale-110"
              :class="[priorityConfig.bgColor]"
            >
              <span
                class="size-3.5 flex-shrink-0"
                :class="[priorityConfig.icon, priorityConfig.color]"
              />
            </button>
          </div>

          <!-- Priority Dropdown (Teleported to body to avoid overflow clipping) -->
          <Teleport to="body">
            <div
              v-if="showPriorityDropdown"
              class="fixed z-dropdown-overlay min-w-[140px] p-1 rounded-xl shadow-lg bg-n-solid-2 border border-n-weak origin-top-left"
              :style="priorityDropdownStyle"
              @click.stop
            >
              <button
                v-for="option in priorityOptions"
                :key="option.value"
                type="button"
                class="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                :class="{
                  'bg-n-alpha-2 font-semibold': priority === option.value,
                }"
                @click="handleSelectPriority(option.value)"
              >
                <span
                  class="size-3.5 flex-shrink-0"
                  :class="[option.icon, option.color]"
                />
                {{ option.label }}
              </button>
            </div>
          </Teleport>

          <!-- Collapse Toggle.
           Bug 1 refinement (2026-04-22): além do opacity-60 (Fase
           anterior), aumentado botão para 28×28 (era 24×24) e ícone
           para 16px (era 12px). Material guideline: ícones tap-friendly
           são ≥16px com botão ≥28px e área de toque expandida. Cliente
           reportou que ícones continuavam pequenos e difíceis de
           identificar mesmo com opacity correta. -->
          <button
            v-tooltip="{
              content: isCollapsed
                ? t('PIPELINE.COLUMN_OPTIONS.EXPAND_ALL')
                : t('PIPELINE.COLUMN_OPTIONS.COLLAPSE_ALL'),
              delay: { show: 300, hide: 0 },
              placement: 'top',
            }"
            class="w-7 h-7 min-w-[28px] rounded-full text-n-slate-10 hover:text-n-slate-12 hover:bg-white/50 dark:hover:bg-n-alpha-2 hover:shadow-sm transition-all opacity-60 group-hover:opacity-100 focus:opacity-100 flex items-center justify-center flex-shrink-0"
            @click="toggleCollapse"
          >
            <FluentIcon
              :icon="isCollapsed ? 'arrow-expand' : 'arrow-outwards'"
              size="16"
            />
          </button>

          <!-- Actions Menu — botão 32×32 + ícone 18px para hierarquia
           clara (action principal do card vs collapse secundário). -->
          <div class="relative flex-shrink-0" @click.stop>
            <button
              v-tooltip="{
                content: t('PIPELINE.CARD.ACTIONS'),
                delay: { show: 200, hide: 0 },
                placement: 'top',
              }"
              class="w-8 h-8 min-w-[32px] rounded-full text-n-slate-10 hover:text-n-slate-12 hover:bg-white/50 dark:hover:bg-n-alpha-2 hover:shadow-sm transition-all opacity-60 group-hover:opacity-100 focus:opacity-100 flex items-center justify-center"
              :class="{
                'opacity-100 bg-n-alpha-2': showActionsMenu,
              }"
              @click="toggleActionsMenu"
            >
              <FluentIcon icon="more-vertical" size="18" />
            </button>

            <!-- Actions Dropdown -->
            <div
              v-if="showActionsMenu"
              class="absolute right-0 top-full mt-1 bg-n-solid-3 rounded-xl shadow-2xl outline outline-1 outline-n-weak py-1 w-40 z-30 transform origin-top-right transition-all"
            >
              <button
                class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-alpha-1 flex items-center gap-2 text-n-slate-11 transition-colors"
                @click="handleViewDetails"
              >
                <FluentIcon icon="eye-show" size="14" class="text-n-slate-9" />
                {{ t('PIPELINE.CARD.VIEW_DETAILS') }}
              </button>
              <button
                class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-alpha-1 flex items-center gap-2 text-n-slate-11 transition-colors"
                @click="handleEdit"
              >
                <FluentIcon icon="edit" size="14" class="text-n-slate-9" />
                {{ t('PIPELINE.CARD.EDIT') }}
              </button>
              <div class="h-px bg-n-weak my-1" />
              <button
                class="w-full min-h-[44px] px-3 py-2 text-left text-xs hover:bg-n-ruby-3 flex items-center gap-2 text-n-ruby-11 transition-colors"
                @click="handleDelete"
              >
                <FluentIcon icon="delete" size="14" />
                {{ t('PIPELINE.CARD.DELETE') }}
              </button>
            </div>
          </div>
        </div>
        <!-- /Card-4.2 grupo de ações -->
      </div>
      <!-- /top row -->

      <!-- Customer Name — Card-4.5 (2026-04-29):
           agora ocupa 100% da largura do card. Suporta line-clamp-2 para
           nomes longos sem truncar abruptamente. -->
      <h3
        ref="titleElement"
        v-tooltip="{
          content: isTitleTruncated ? customerName : null,
          delay: { show: 500, hide: 0 },
          placement: 'top',
        }"
        class="text-heading-3 font-semibold text-n-slate-12 line-clamp-2 leading-tight"
        @mouseenter="checkTruncation"
      >
        {{ customerName }}
      </h3>
    </div>

    <!-- Card body — hidden when collapsed -->
    <div v-if="!isCollapsed">
      <!-- Description Preview -->
      <p
        v-if="descriptionPreview"
        v-tooltip="{
          content: props.card?.item_details?.description,
          delay: { show: 300, hide: 0 },
          placement: 'top',
        }"
        class="text-body-main text-n-slate-11 mb-3 line-clamp-2 leading-relaxed"
      >
        {{ descriptionPreview }}
      </p>

      <!-- 5.4: próxima ação pendente do checklist -->
      <div
        v-if="nextChecklistItem"
        v-tooltip="{
          content: nextChecklistItem.text,
          delay: { show: 300, hide: 0 },
          placement: 'top',
        }"
        class="flex items-center gap-1.5 mb-3 text-xs text-n-slate-11"
      >
        <FluentIcon
          icon="list"
          size="12"
          class="flex-shrink-0 text-n-slate-10"
        />
        <span class="truncate">{{ nextChecklistItem.text }}</span>
      </div>

      <!-- Card-4.8 (2026-04-29): tags com flex-wrap, mostra todas
           quebrando em múltiplas linhas. Pedido cliente: prefere ver
           tudo a ter altura previsível. -->
      <div
        v-if="filteredConversationLabels.length > 0"
        v-tooltip="allLabelsText"
        :aria-label="allLabelsText"
        class="flex flex-wrap items-center gap-1 mb-3 cursor-help"
      >
        <span
          v-for="label in visibleLabels"
          :key="label"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-label-small flex-shrink-0 truncate max-w-[110px] border border-black/5 dark:border-white/5 backdrop-blur-sm shadow-sm"
          :style="
            getLabelColor(label)
              ? {
                  backgroundColor: `${getLabelColor(label)}20`,
                  color: darkenColor(getLabelColor(label)),
                }
              : {}
          "
          :class="!getLabelColor(label) ? 'bg-n-alpha-2 text-n-slate-11' : ''"
        >
          {{ label }}
        </span>
      </div>

      <!-- Card-4.3 (2026-04-22): combina status + scheduled + deadline
           + value + lifetime na MESMA linha. Antes Meta Info Row era
           dedicada e flutuava com `ml-auto` solitário. Agora 1 linha
           coerente: status à esquerda, badges/value/lifetime cola à
           direita via order/ml-auto. -->
      <div class="flex flex-wrap items-center gap-2 mb-3">
        <!-- Status Badge -->
        <div class="relative" @click.stop>
          <component
            :is="canChangeStatus ? 'button' : 'div'"
            class="text-label-small font-semibold px-2.5 py-1 rounded-full transition-all border flex items-center gap-1.5"
            :class="[
              statusClasses,
              canChangeStatus
                ? 'cursor-pointer hover:opacity-80 shadow-sm'
                : 'cursor-default shadow-none',
            ]"
            @click.stop="canChangeStatus ? toggleStatusMenu() : null"
          >
            <span
              class="w-1.5 h-1.5 rounded-full"
              :class="{
                'bg-n-slate-9': cardStatus === 'open',
                'bg-n-teal-9': cardStatus === 'won',
                'bg-n-ruby-9': cardStatus === 'lost',
              }"
            />
            {{ statusLabel }}
            <FluentIcon
              v-if="canChangeStatus"
              icon="chevron-down"
              size="8"
              class="opacity-50"
            />
          </component>

          <!-- Status Dropdown -->
          <div
            v-if="showStatusMenu"
            class="absolute left-0 top-full mt-1 bg-n-solid-3 rounded-xl shadow-xl outline outline-1 outline-n-weak py-1 w-28 z-20"
          >
            <button
              v-for="option in statusOptions"
              :key="option.value"
              class="w-full px-3 py-2 text-left text-label-small hover:bg-n-alpha-1 flex items-center gap-2 transition-colors"
              @click.stop="changeStatus(option.value)"
            >
              <span
                class="w-2 h-2 rounded-full"
                :class="{
                  'bg-n-slate-9': option.value === 'open',
                  'bg-n-teal-9': option.value === 'won',
                  'bg-n-ruby-9': option.value === 'lost',
                }"
              />
              <span class="text-n-slate-11">{{ option.label }}</span>
            </button>
          </div>
        </div>

        <!-- Scheduled badge — Card-4.7 (2026-04-29):
             "Agendado" representa um compromisso (sync Google Calendar).
             Click → navega para view Agenda (não edita inline) — UX
             alinhada à semântica do campo. -->
        <button
          v-if="hasScheduledDate"
          v-tooltip="{
            content: scheduledAt?.fullDateTime
              ? `${t('PIPELINE.CARD.SCHEDULED_AT')} — ${scheduledAt.fullDateTime} · Clique para abrir na Agenda`
              : `${t('PIPELINE.CARD.SCHEDULED_AT')} · Clique para abrir na Agenda`,
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          type="button"
          class="text-label-small font-medium px-2.5 py-1 rounded-full bg-violet-3/80 text-violet-11 border border-violet-6/50 flex items-center gap-1 shadow-sm backdrop-blur-sm hover:bg-violet-4 hover:scale-105 transition-transform cursor-pointer"
          @click.stop="handleOpenAgenda"
        >
          <FluentIcon icon="calendar-clock" size="12" />
          <span class="font-semibold opacity-80"
            >{{ t('PIPELINE.CARD.SCHEDULED_AT') }}:</span
          >
          <span>{{ scheduledAt.text }}</span>
        </button>
        <!-- Deadline badge — Card-4.7 (2026-04-29):
             "Prazo" é propriedade interna do card. Click → form de edição
             onde agente ajusta deadline / forecast_close_date. -->
        <button
          v-if="deadline?.text"
          v-tooltip="{
            content: deadline.fullDateTime
              ? `${t('PIPELINE.CARD.DEADLINE')} — ${deadline.fullDateTime} · Clique para editar`
              : `${t('PIPELINE.CARD.DEADLINE')} · Clique para editar`,
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          type="button"
          class="text-label-small font-medium px-2.5 py-1 rounded-full border flex items-center gap-1 shadow-sm backdrop-blur-sm hover:scale-105 transition-transform cursor-pointer"
          :class="deadlineBadgeClasses"
          @click.stop="handleEditDeadline"
        >
          <FluentIcon v-if="isOverdue" icon="warning" size="12" />
          <FluentIcon v-else icon="alarm" size="12" />
          <span class="font-semibold opacity-80"
            >{{ t('PIPELINE.CARD.DEADLINE') }}:</span
          >
          <span>{{ deadline.text }}</span>
        </button>
        <!-- Value (cola à direita quando presente, via `ml-auto`) -->
        <span
          v-if="formattedValue"
          class="ml-auto text-label-small font-bold text-n-slate-12 bg-white/60 dark:bg-n-solid-3/80 backdrop-blur-sm px-2.5 py-1 rounded-full border border-n-weak/50 shadow-sm"
        >
          {{ formattedValue }}
        </span>
        <!-- Card-4.3: Card lifetime cola à direita só se NÃO há value
             (caso contrário, lifetime quebra pra próxima linha via flex-wrap) -->
        <span
          v-if="cardLifetime"
          class="text-label-small text-n-slate-9 flex items-center gap-1"
          :class="{ 'ml-auto': !formattedValue }"
        >
          <FluentIcon icon="calendar-clock" size="12" />
          {{ cardLifetime }}
        </span>
      </div>

      <!-- Footer: Agent & Conversation. Card-4.3 (2026-04-22):
           pt-2 mt-2 (era pt-3 sem mt) e border-t/50 mais sutil para
           reduzir o "peso" visual do divisor. -->
      <div
        class="flex justify-between items-center pt-2 mt-2 border-t border-n-weak/50"
      >
        <!-- Agent — UX 2026-04-22: agora clicável.
             Antes click bubble pro article e abria detalhes do card.
             Agora abre dropdown teleportado com lista de agentes
             (busca + select + remover atribuição). -->
        <div
          ref="agentButtonRef"
          v-tooltip="{
            content: agentInfo
              ? t('PIPELINE.CARD.AGENT_DROPDOWN.CHANGE_TOOLTIP', {
                  name: agentInfo.name,
                })
              : t('PIPELINE.CARD.AGENT_DROPDOWN.ASSIGN_TOOLTIP'),
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          class="flex items-center gap-1.5 group/agent cursor-pointer rounded-full hover:bg-white/50 dark:hover:bg-n-alpha-2 hover:shadow-sm -mx-1 px-2 py-1 transition-all min-w-0"
          :class="activeSequenceProgress ? 'flex-shrink-0' : 'flex-1 mr-2'"
          role="button"
          :tabindex="0"
          @click.stop="toggleAgentDropdown"
          @keydown.enter.stop.prevent="toggleAgentDropdown"
          @keydown.space.stop.prevent="toggleAgentDropdown"
        >
          <template v-if="agentInfo">
            <div
              v-if="agentInfo.thumbnail || agentInfo.avatar_url"
              class="w-5 h-5 rounded-full overflow-hidden ring-1 ring-n-weak flex-shrink-0"
            >
              <img
                :src="agentInfo.thumbnail || agentInfo.avatar_url"
                :alt="agentInfo.name"
                class="w-full h-full object-cover"
              />
            </div>
            <div
              v-else
              class="w-5 h-5 rounded-full bg-n-alpha-2 flex items-center justify-center ring-1 ring-n-weak flex-shrink-0"
            >
              <span class="text-[10px] font-bold text-n-slate-10">
                {{ agentInfo.name?.charAt(0)?.toUpperCase() || 'A' }}
              </span>
            </div>
            <!-- Hide agent name in compact mode (active sequence + linked
                 conversation already crowd the footer; avatar + tooltip
                 keep the agent identifiable). -->
            <span
              v-if="!activeSequenceProgress"
              class="text-label-small text-n-slate-10 truncate min-w-0 group-hover/agent:text-n-slate-12 transition-colors"
            >
              {{ agentInfo.name }}
            </span>
          </template>
          <template v-else>
            <!-- UX A2 (2026-04-22): empty state era avatar dashed quase
                 invisível (opacity-40) sem texto — dava impressão de
                 card incompleto. Agora ícone person-add visível +
                 label italic dão affordance clara de "atribuir agente". -->
            <div
              class="w-5 h-5 rounded-full bg-n-alpha-1 flex items-center justify-center border border-dashed border-n-slate-7 flex-shrink-0"
            >
              <FluentIcon icon="person-add" size="12" class="text-n-slate-10" />
            </div>
            <span
              class="text-label-small text-n-slate-10 italic truncate min-w-0"
            >
              {{ t('PIPELINE.CARD.NO_AGENT') }}
            </span>
          </template>
        </div>

        <!-- Agent Dropdown — UX 2026-04-22 (Teleported to body) -->
        <Teleport to="body">
          <div
            v-if="showAgentDropdown"
            class="fixed z-dropdown-overlay w-64 max-h-[280px] flex flex-col rounded-xl shadow-lg bg-n-solid-2 border border-n-weak origin-bottom-left overflow-hidden"
            :style="agentDropdownStyle"
            @click.stop
          >
            <!-- Search input -->
            <div class="p-2 border-b border-n-weak">
              <input
                v-model="agentSearchQuery"
                type="text"
                :placeholder="
                  t('PIPELINE.CARD.AGENT_DROPDOWN.SEARCH_PLACEHOLDER')
                "
                class="w-full px-2 py-1.5 text-sm bg-n-alpha-1 text-n-slate-12 placeholder-n-slate-9 rounded-md outline-none focus:bg-n-alpha-2 transition-colors"
                @click.stop
              />
            </div>
            <!-- Agents list (scrollable) -->
            <div class="flex-1 overflow-y-auto p-1">
              <button
                v-for="agent in filteredAgents"
                :key="agent.id"
                type="button"
                class="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-n-slate-12 text-left hover:bg-n-alpha-2 transition-colors"
                :class="{
                  'bg-n-alpha-2 font-semibold':
                    agentInfo && agentInfo.id === agent.id,
                }"
                @click.stop="handleSelectAgent(agent.id)"
              >
                <div
                  v-if="agent.thumbnail || agent.avatar_url"
                  class="w-6 h-6 rounded-full overflow-hidden ring-1 ring-n-weak flex-shrink-0"
                >
                  <img
                    :src="agent.thumbnail || agent.avatar_url"
                    :alt="agent.name"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div
                  v-else
                  class="w-6 h-6 rounded-full bg-n-alpha-2 flex items-center justify-center ring-1 ring-n-weak flex-shrink-0"
                >
                  <span class="text-[10px] font-bold text-n-slate-10">
                    {{ agent.name?.charAt(0)?.toUpperCase() || 'A' }}
                  </span>
                </div>
                <span class="truncate flex-1">{{ agent.name }}</span>
                <FluentIcon
                  v-if="agentInfo && agentInfo.id === agent.id"
                  icon="checkmark"
                  size="14"
                  class="text-n-brand flex-shrink-0"
                />
              </button>
              <p
                v-if="filteredAgents.length === 0"
                class="text-center text-xs text-n-slate-9 italic py-3"
              >
                {{ t('PIPELINE.CARD.AGENT_DROPDOWN.NO_AGENTS_FOUND') }}
              </p>
            </div>
            <!-- Unassign action (only when agent assigned) -->
            <div v-if="agentInfo" class="border-t border-n-weak p-1">
              <button
                type="button"
                class="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-n-ruby-11 text-left hover:bg-n-ruby-3 transition-colors"
                @click.stop="handleUnassignAgent"
              >
                <FluentIcon icon="dismiss" size="14" />
                {{ t('PIPELINE.CARD.AGENT_DROPDOWN.UNASSIGN') }}
              </button>
            </div>
          </div>
        </Teleport>

        <!-- Sequence Progress Badge (Phase 1.2)
             Visible when this card has an active or paused sequence.
             Click opens card details focused on the Sequences tab. -->
        <button
          v-if="activeSequenceProgress"
          v-tooltip="{
            content: sequenceTooltip,
            delay: { show: 200, hide: 0 },
            placement: 'top',
          }"
          type="button"
          class="flex-shrink-0 flex items-center gap-1 text-label-small font-medium px-2 py-1 rounded-full border shadow-sm transition-all hover:scale-105"
          :class="
            activeSequenceProgress.status === 'active'
              ? 'bg-n-iris-3 text-n-iris-11 border-n-iris-6/50 hover:bg-n-iris-4'
              : 'bg-n-amber-3 text-n-amber-11 border-n-amber-6/50 hover:bg-n-amber-4'
          "
          @click.stop="handleOpenSequences"
        >
          <FluentIcon icon="flash-on" size="12" />
          <span
            >{{ activeSequenceProgress.current_step }}/{{
              activeSequenceProgress.total_steps
            }}</span
          >
          <!-- Mini progress bar -->
          <div class="w-8 h-1 rounded-full bg-black/10 overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-300"
              :class="
                activeSequenceProgress.status === 'active'
                  ? 'bg-n-iris-9'
                  : 'bg-n-amber-9'
              "
              :style="{ width: progressPercent + '%' }"
            />
          </div>
        </button>

        <!-- Conversation Link -->
        <button
          v-if="hasConversation"
          v-tooltip="{
            content: t('PIPELINE.CARD.CONVERSATION_BADGE_TOOLTIP', {
              id: card.conversation_display_id,
            }),
            delay: { show: 300, hide: 0 },
            placement: 'top',
          }"
          :aria-label="
            t('PIPELINE.CARD.CONVERSATION_BADGE_TOOLTIP', {
              id: card.conversation_display_id,
            })
          "
          class="flex-shrink-0 flex items-center gap-1.5 text-n-brand bg-n-brand/10 hover:bg-n-brand/20 transition-all px-2.5 py-1 rounded-full shadow-sm"
          @click="openConversation"
        >
          <FluentIcon icon="chat" size="12" />
          <span class="text-label-small font-semibold">
            #{{ card.conversation_display_id }}
          </span>
        </button>
      </div>
    </div>
    <!-- Hidden details for screen readers -->
    <div :id="`card-details-${card.id}`" class="sr-only">
      {{ descriptionPreview }}
      {{
        agentInfo
          ? t('PIPELINE.CARD.ASSIGNED_TO', { name: agentInfo.name })
          : t('PIPELINE.CARD.NO_AGENT')
      }}
    </div>
  </article>
</template>

<!-- CSS migrated to Tailwind classes -->
